import { useEffect, useMemo, useState } from 'react';
import { Alert, FormHelperText, Grid, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup } from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel';
import { HOME_ADDABLE_SECTION_TYPES, TIPO_SECAO_HOME_LABEL, TipoSecaoHome } from '../../../../model/apm/apm-enums';
import { IHomeSectionResponse } from '../../../../model/apm/home/response/home-section-response';
import { ConfiguredFields } from '../../components/configured-fields';
import { FormDrawer, FormDrawerGroup, VisibilitySwitch } from '../../../../components/form-drawer';
import { pickFieldGroup, validateFields } from '../../components/field-config';
import { SectionTypeCard } from '../../components/section-type-card';
import {
    GALLERY_COLUMNS_MAX, GALLERY_COLUMNS_MIN, GalleryConfiguration, HOME_SECTION_CONFIG, HomeSectionField,
    parseGalleryConfiguration, serializeGalleryConfiguration,
} from './home-section-config';
import { HOME_SECTION_ICONS } from './home-section-icons';

const IDENTIFICATION_FIELDS: HomeSectionField[] = ['title', 'subtitle'];

const GALLERY_COLUMN_OPTIONS = Array.from({ length: GALLERY_COLUMNS_MAX - GALLERY_COLUMNS_MIN + 1 }, (_, i) => GALLERY_COLUMNS_MIN + i);

function GalleryLayoutFields({ value, onChange }: { value: GalleryConfiguration; onChange: (next: GalleryConfiguration) => void }) {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
                <InputLabel>Formato</InputLabel>
                <ToggleButtonGroup
                    exclusive
                    fullWidth
                    size="small"
                    value={value.layout}
                    onChange={(_, layout) => layout && onChange({ ...value, layout })}
                >
                    <ToggleButton value="grid"><GridViewIcon fontSize="small" sx={{ mr: 1 }} />Grade</ToggleButton>
                    <ToggleButton value="carousel"><ViewCarouselIcon fontSize="small" sx={{ mr: 1 }} />Carrossel</ToggleButton>
                </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md={5}>
                <InputLabel id="home-section-gallery-columns">Imagens por linha</InputLabel>
                <Select
                    fullWidth
                    size="small"
                    labelId="home-section-gallery-columns"
                    value={value.columns}
                    onChange={(e) => onChange({ ...value, columns: Number(e.target.value) })}
                >
                    {GALLERY_COLUMN_OPTIONS.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
            </Grid>
            <Grid item xs={12}>
                <FormHelperText sx={{ mt: 0 }}>
                    {value.layout === 'carousel'
                        ? 'As imagens passam lado a lado; em telas menores o portal reduz a quantidade visível.'
                        : 'As imagens são distribuídas em linhas; em telas menores o portal reduz a quantidade por linha.'}
                </FormHelperText>
            </Grid>
        </Grid>
    );
}

interface HomeSectionDrawerProps {
    open: boolean;
    section?: IHomeSectionResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: Record<string, any>) => void;
}

export function HomeSectionDrawer({ open, section, isSaving, onClose, onSave }: HomeSectionDrawerProps) {
    const [form, setForm] = useState<Record<string, any>>({});
    const [showErrors, setShowErrors] = useState(false);
    const isEdit = !!section;

    useEffect(() => {
        if (!open) return;
        setForm(section ? { ...section } : { type: HOME_ADDABLE_SECTION_TYPES[0], isVisible: true });
        setShowErrors(false);
    }, [open, section]);

    const type: TipoSecaoHome = form.type ?? HOME_ADDABLE_SECTION_TYPES[0];
    const config = HOME_SECTION_CONFIG[type];
    const identificationFields = pickFieldGroup(config.fields, IDENTIFICATION_FIELDS, true);
    const contentFields = pickFieldGroup(config.fields, IDENTIFICATION_FIELDS, false);
    const hasIdentification = Object.keys(identificationFields).length > 0;
    const hasContent = Object.keys(contentFields).length > 0;
    const errors = useMemo(() => validateFields(config.fields, form), [config, form]);
    const isGallery = type === TipoSecaoHome.GaleriaImagens;

    function handleChange(patch: Record<string, any>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSelectType(next: TipoSecaoHome) {
        // Troca de tipo descarta campos que não se aplicam ao novo tipo
        setForm((current) => ({ type: next, isVisible: current.isVisible, title: current.title, subtitle: current.subtitle }));
        setShowErrors(false);
    }

    function handleSave() {
        if (Object.keys(errors).length > 0) {
            setShowErrors(true);
            return;
        }
        // A galeria grava o layout (mesmo o padrão) para o portal não depender de configuração ausente
        onSave(isGallery ? { ...form, configuration: serializeGalleryConfiguration(parseGalleryConfiguration(form.configuration)) } : form);
    }

    return (
        <FormDrawer
            open={open}
            icon={HOME_SECTION_ICONS[type]}
            title={isEdit ? 'Editar seção' : 'Nova seção da home'}
            subtitle={isEdit ? `${TIPO_SECAO_HOME_LABEL[type]}${section?.isFixed ? ' · seção fixa' : ''}` : 'Escolha o tipo e preencha o conteúdo da seção'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar seção'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            {!isEdit && (
                <FormDrawerGroup title="Tipo de seção" description="As seções Diário Oficial e Notícias são fixas e já fazem parte da home. Header, carrossel do topo e rodapé são do portal e não mudam.">
                    <Grid container spacing={1.5}>
                        {HOME_ADDABLE_SECTION_TYPES.map((t) => (
                            <Grid item xs={12} sm={6} key={t}>
                                <SectionTypeCard
                                    icon={HOME_SECTION_ICONS[t]}
                                    label={TIPO_SECAO_HOME_LABEL[t]}
                                    description={HOME_SECTION_CONFIG[t].description}
                                    selected={t === type}
                                    onSelect={() => handleSelectType(t)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </FormDrawerGroup>
            )}

            {isEdit && (
                <Alert severity="info" icon={HOME_SECTION_ICONS[type]}>{config.description}</Alert>
            )}

            {hasIdentification && (
                <FormDrawerGroup title="Identificação" description="Título e subtítulo exibidos acima da seção no portal (opcionais).">
                    <Grid container spacing={2}>
                        <ConfiguredFields
                            idPrefix="home-section"
                            fields={identificationFields}
                            value={form}
                            errors={errors}
                            showErrors={showErrors}
                            onChange={handleChange}
                        />
                    </Grid>
                </FormDrawerGroup>
            )}

            {hasContent && (
                <FormDrawerGroup title="Conteúdo">
                    <Grid container spacing={2}>
                        <ConfiguredFields
                            idPrefix="home-section"
                            fields={contentFields}
                            value={form}
                            errors={errors}
                            showErrors={showErrors}
                            onChange={handleChange}
                        />
                    </Grid>
                </FormDrawerGroup>
            )}

            {isGallery && (
                <FormDrawerGroup title="Layout" description="Como as imagens são apresentadas na home.">
                    <GalleryLayoutFields
                        value={parseGalleryConfiguration(form.configuration)}
                        onChange={(next) => handleChange({ configuration: serializeGalleryConfiguration(next) })}
                    />
                </FormDrawerGroup>
            )}

            {!hasIdentification && !hasContent && (
                <Alert severity="info">Esta seção não possui campos configuráveis; altere apenas a visibilidade e a posição na lista.</Alert>
            )}

            {config.itemNoun && (
                <Alert severity="success" variant="outlined">
                    {isEdit
                        ? `Os itens desta seção (${config.itemNoun.toLowerCase()}s) são gerenciados na lista da home, expandindo a seção.`
                        : `Depois de salvar, expanda a seção na lista da home para adicionar os itens (${config.itemNoun.toLowerCase()}s).`}
                </Alert>
            )}

            <FormDrawerGroup title="Exibição">
                <VisibilitySwitch
                    checked={form.isVisible !== false}
                    onChange={(isVisible) => handleChange({ isVisible })}
                    description="Seções ocultas continuam salvas, mas não aparecem na home."
                />
            </FormDrawerGroup>
        </FormDrawer>
    );
}
